import { trpc } from '@/trpcClient';
import { StatusBadge, STATUS_LABELS } from '@/components/StatusBadge';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent } from '@shelflix/ui-web/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shelflix/ui-web/components/ui/table';
import { TablePagination } from '@shelflix/ui-web/components/TablePagination';
import Icon from '@shelflix/ui-web/components/Icon';
import { EMaterialStatus } from '@shelflix/schemas/material/enums';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';

interface StatusLogsTableProps {
  materialUuid: string;
}

export function StatusLogsTable({ materialUuid }: StatusLogsTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [oldStatus, setOldStatus] = useState<EMaterialStatus | undefined>();
  const [newStatus, setNewStatus] = useState<EMaterialStatus | undefined>();
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, isError } = useQuery({
    ...trpc.material.listStatusLogs.queryOptions({
      materialUuid,
      page,
      pageSize,
      dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
      oldStatus,
      newStatus,
    }),
    refetchInterval: 30000, // Refetch every 30 seconds to stay in sync with polling
  });

  const hasActiveFilters = dateFrom || dateTo || oldStatus || newStatus;

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setOldStatus(undefined);
    setNewStatus(undefined);
    setPage(1);
  };

  const formatDateTime = (dateString: string) => {
    try {
      // Parse the string as UTC if it doesn't have timezone info, then format in local timezone
      const utcDateString = dateString.includes('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
      const date = new Date(utcDateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const calculateElapsedTime = (currentDate: string, previousDate?: string) => {
    if (!previousDate) return '-';

    try {
      const current = new Date(currentDate);
      const previous = new Date(previousDate);
      const diffMs = current.getTime() - previous.getTime();

      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

      return parts.join(' ');
    } catch {
      return '-';
    }
  };

  if (isError) {
    return (
      <Card className="p-6">
        <p className="text-destructive text-sm">Erro ao carregar histórico de status</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Filter Toggle */}
      <div className="flex items-center justify-between px-4">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-2">
          <Icon name="filter_alt" size={16} />
          <span>Filtros</span>
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2 text-xs">
            <Icon name="close" size={14} />
            <span>Limpar filtros</span>
          </Button>
        )}
      </div>

      {/* Filters Card */}
      {showFilters && (
        <Card className="rounded-[10px] border-none px-8 shadow-none">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="dateFrom" className="text-xs font-medium text-[var(--gray-primary)]">
                  Data início
                </label>
                <input
                  id="dateFrom"
                  type="datetime-local"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="dateTo" className="text-xs font-medium text-[var(--gray-primary)]">
                  Data fim
                </label>
                <input
                  id="dateTo"
                  type="datetime-local"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="oldStatus" className="text-xs font-medium text-[var(--gray-primary)]">
                  Status anterior
                </label>
                <Select
                  value={oldStatus || 'all'}
                  onValueChange={(value) => {
                    setOldStatus(value === 'all' ? undefined : (value as EMaterialStatus));
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="oldStatus" className="h-10 w-full">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="newStatus" className="text-xs font-medium text-[var(--gray-primary)]">
                  Novo status
                </label>
                <Select
                  value={newStatus || 'all'}
                  onValueChange={(value) => {
                    setNewStatus(value === 'all' ? undefined : (value as EMaterialStatus));
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="newStatus" className="h-10 w-full">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="mx-auto w-[80%] rounded-[10px]">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-neutral-600">
              <TableHead className="w-1/4 border-r border-neutral-600 last:border-r-0">Data/Hora</TableHead>
              <TableHead className="w-1/4 border-r border-neutral-600 last:border-r-0">Status Anterior</TableHead>
              <TableHead className="w-1/4 border-r border-neutral-600 last:border-r-0">Novo Status</TableHead>
              <TableHead className="w-1/4 border-r border-neutral-600 last:border-r-0">
                Tempo corrido no Status Anterior
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Carregando...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !data?.logs.length ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  <span className="text-muted-foreground text-sm">
                    {hasActiveFilters
                      ? 'Nenhum registro encontrado com os filtros aplicados'
                      : 'Nenhum registro de alteração de status'}
                  </span>
                </TableCell>
              </TableRow>
            ) : (
              data.logs.map((log, index) => (
                <TableRow key={log.id}>
                  <TableCell className="border-r border-neutral-600 font-medium last:border-r-0">
                    {formatDateTime(log.datetime)}
                  </TableCell>
                  <TableCell className="border-r border-neutral-600 last:border-r-0">
                    {log.oldStatus ? (
                      <StatusBadge status={log.oldStatus} />
                    ) : (
                      <span className="text-muted-foreground text-sm italic">-</span>
                    )}
                  </TableCell>
                  <TableCell className="border-r border-neutral-600 last:border-r-0">
                    <StatusBadge status={log.newStatus} />
                  </TableCell>
                  <TableCell className="border-r border-neutral-600 last:border-r-0">
                    <span className="text-muted-foreground text-sm">
                      {calculateElapsedTime(log.datetime, data.logs[index + 1]?.datetime)}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex flex-col items-center gap-4">
          <TablePagination page={page} totalItems={data.total} itemsPerPage={pageSize} onPageChange={setPage} />
          <p className="text-muted-foreground text-sm">
            Mostrando {(page - 1) * pageSize + 1} a {Math.min(page * pageSize, data.total)} de {data.total} registros
          </p>
        </div>
      )}
    </div>
  );
}
